import { format } from 'date-fns'
import { TOOLTIP } from '../../../../constants/tooltips'
import { AquiferTestFieldsEdit, FieldTypes } from '../../../../types/form-types'
import { ValidationErrors } from '../../../hooks/use-edit-form'
import { DISPLAY_DATE_FORMAT, INTERNAL_DATE_FORMAT } from '../../../services/constants'
import { parseDate } from '../../aoraki/generic-search-common/src'
import LookupField from '../../wells/lookup-field'
import { preventChangeOnScroll } from '../../../services/form-utils'
import { Card, DateField, FormField, FormFieldRow, Heading, InputWidth, TextInput } from '@msaf/core-react'

export interface AquiferEditFormInterface {
  fields: Partial<AquiferTestFieldsEdit>
  isLoading: boolean
  setValue: (key: keyof AquiferTestFieldsEdit, value: FieldTypes) => void
  validationErrors?: ValidationErrors
}

export default function AquiferTestDetailsEditForm({
  fields,
  isLoading,
  setValue,
  validationErrors = {},
}: AquiferEditFormInterface) {
  return (
    <>
      <Heading isSkeleton={isLoading} level={2}>
        Aquifer test
      </Heading>
      <Heading isSkeleton={isLoading} level={3}>
        Details
      </Heading>
      <FormField
        isSkeleton={isLoading}
        label='Test date'
        labelId='testDate'
        validationMessages={validationErrors?.testDate}
        isRequired
      >
        <DateField
          isSkeleton={isLoading}
          isDisabled={isLoading}
          fieldSize={InputWidth.large}
          dateFormat={DISPLAY_DATE_FORMAT}
          selectedDate={parseDate(fields.testDate)}
          setSelectedDate={(date: Date) => setValue('testDate', date ? format(date, INTERNAL_DATE_FORMAT) : '')}
          isInvalid={!!validationErrors?.testDate}
        />
      </FormField>

      <FormField isSkeleton={isLoading} label='Test type' labelId='testTypeLabel'>
        <LookupField
          isSkeleton={isLoading}
          fieldId='testType'
          labelledBy='testTypeLabel'
          value={`${fields.testType ?? ''}`}
          setValue={({ value }) => setValue('testType', value)}
        />
      </FormField>
      <Card>
        <Heading isSkeleton={isLoading} level={4}>
          Flow Rate
        </Heading>
        <FormFieldRow>
          <FormField
            toolTipMessage={TOOLTIP.maxFlowRate}
            isSkeleton={isLoading}
            label='Max flow rate'
            htmlFor='maxFlowRate'
            validationMessages={validationErrors?.maxFlowRate}
            isRequired
          >
            <TextInput
              isSkeleton={isLoading}
              fieldSize={InputWidth.large}
              type='number'
              onWheel={preventChangeOnScroll}
              id='maxFlowRate'
              value={`${fields.maxFlowRate ?? ''}`}
              setValue={(value) => setValue('maxFlowRate', value)}
              isInvalid={!!validationErrors?.maxFlowRate}
            />
          </FormField>

          <FormField
            isSkeleton={isLoading}
            label='Flow rate unit'
            labelId='pumpingRateUnit'
            validationMessages={validationErrors?.pumpingRateUnit}
            isRequired
          >
            <LookupField
              isSkeleton={isLoading}
              fieldId='pumpingRateUnit'
              labelledBy='pumpingRateUnitLabel'
              value={`${fields.pumpingRateUnit ?? ''}`}
              setValue={({ value }) => setValue('pumpingRateUnit', value)}
              isInvalid={!!validationErrors?.pumpingRateUnit}
            />
          </FormField>
        </FormFieldRow>
      </Card>

      <FormField
        isSkeleton={isLoading}
        label='Pumping duration (mins)'
        htmlFor='pumpingDuration'
        validationMessages={validationErrors?.pumpingDuration}
      >
        <TextInput
          isSkeleton={isLoading}
          type='number'
          onWheel={preventChangeOnScroll}
          id='pumpingDuration'
          value={`${fields.pumpingDuration ?? ''}`}
          setValue={(value) => setValue('pumpingDuration', value)}
          isInvalid={!!validationErrors?.pumpingDuration}
        />
      </FormField>

      <FormField isSkeleton={isLoading} label='Aquifer type' labelId='aquiferTypeLabel'>
        <LookupField
          isSkeleton={isLoading}
          fieldId='aquiferType'
          labelledBy='aquiferTypeLabel'
          value={`${fields.aquiferType ?? ''}`}
          setValue={({ value }) => setValue('aquiferType', value)}
        />
      </FormField>

      <FormField isSkeleton={isLoading} label='Boundary condition' labelId='boundaryConditionLabel'>
        <LookupField
          isSkeleton={isLoading}
          fieldId='boundaryCondition'
          labelledBy='boundaryConditionLabel'
          value={`${fields.boundaryCondition ?? ''}`}
          setValue={({ value }) => setValue('boundaryCondition', value)}
        />
      </FormField>
    </>
  )
}
